import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useNotificationsUnreadCount,
} from '@/api/hooks';
import { cn } from '@/lib/cn';
import { formatDateTime } from '@/lib/format';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { data: unreadCount = 0 } = useNotificationsUnreadCount();
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();

  const items = data?.notifications ?? [];

  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  async function openItem(id: string, actionUrl: string | null, isUnread: boolean) {
    if (isUnread) {
      try {
        await markRead.mutateAsync(id);
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (actionUrl) navigate(actionUrl);
  }

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-manejai-sand bg-white text-manejai-forest transition-colors hover:bg-manejai-cream/50"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Notificações"
      >
        <Bell className="h-4 w-4" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-[min(100vw-2rem,22rem)] overflow-hidden rounded-xl border border-manejai-sand bg-white shadow-elevated z-50"
        >
          <div className="flex items-center justify-between border-b border-manejai-sand/60 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-manejai-forest">Notificações</p>
              <p className="text-xs text-manejai-bark/55">
                {unreadCount > 0 ? `${unreadCount} não lida(s)` : 'Tudo em dia'}
              </p>
            </div>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => markAll.mutate()}
                className="text-xs font-medium text-manejai-moss hover:underline"
              >
                Marcar todas
              </button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-manejai-bark/55">
                Nenhuma notificação por enquanto.
              </p>
            ) : (
              items.map((item) => {
                const unread = !item.read_at;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openItem(item.id, item.action_url, unread)}
                    className={cn(
                      'flex w-full flex-col gap-0.5 border-b border-manejai-sand/40 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-manejai-cream/40',
                      unread && 'bg-manejai-forest/[0.03]',
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {unread && (
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
                      )}
                      <div className={cn('min-w-0 flex-1', !unread && 'pl-3.5')}>
                        <p className="text-sm font-medium text-manejai-forest">{item.title}</p>
                        {item.body && (
                          <p className="mt-0.5 text-xs text-manejai-bark/70 line-clamp-2">
                            {item.body}
                          </p>
                        )}
                        <p className="mt-1 text-[11px] text-manejai-bark/45">
                          {formatDateTime(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          <div className="border-t border-manejai-sand/60 px-4 py-2.5">
            <Link
              to="/configuracoes#notificacoes"
              onClick={() => setOpen(false)}
              className="text-xs font-medium text-manejai-moss hover:underline"
            >
              Preferências de notificação
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
